import React from "react"
import * as O from "fp-ts/Option"
import * as E from "fp-ts/Either"
import * as A from "fp-ts/Array"
import { pipe } from "fp-ts/function"
import { Icon } from "../../components"
import * as M from "../Model"
import { isNode } from "../../lib/dom"
import * as Router from "../../routes"

type Props = Pick<
  M.Model,
  "properties" | "dashboard" | "tab" | "navigation"
> & {
  children?: React.ReactNode
  tabChanged: (tab: Router.Tab) => void
  dashboardChanged: (propcode: string) => void
  logoutMsg: () => void
}

export default function view({
  children,
  tab,
  navigation,
  dashboard,
  properties,
  tabChanged,
  dashboardChanged,
  logoutMsg,
}: Props): JSX.Element {
  const isCurrentTab = (currentTab: Router.Tab): string =>
    pipe(
      tab,
      O.match(
        () => "",
        t => (t === currentTab ? "current" : "")
      )
    )

  return (
    <div className={containerStyles(tab)}>
      <div className="max-w-[1352px] mx-auto">
        <header>
          <div className="flex items-center pr-4 mb-[100px] sm:mb-0 ">
            <a
              href="/"
              aria-label="Flyp Logo Home"
              className=" w-20 sm:w-[102px]"
            >
              <Icon.Logo />
            </a>
            <div className="flex-1">
              {O.isSome(tab) && (
                <div
                  className="absolute sm:relative top-[100px] sm:top-0 left-0 w-full flex items-center justify-center gap-4"
                  role="tablist"
                >
                  {navigation.map((item, idx) =>
                    item.enabled ? (
                      <button
                        key={item.tab}
                        data-cy={`dashboard-${item.tab}-tab`}
                        className={`tab rounded-none ${isCurrentTab(
                          item.tab
                        )} `}
                        onClick={() => tabChanged(item.tab)}
                        role="tab"
                        aria-controls="page-tab-view"
                        aria-selected={isCurrentTab(item.tab) === "current"}
                      >
                        {Router.dahboardTabValue(item.tab)}
                      </button>
                    ) : (
                      <React.Fragment key={idx}></React.Fragment>
                    )
                  )}
                </div>
              )}
            </div>
            <Menu
              properties={properties}
              dashboard={dashboard}
              logoutMsg={logoutMsg}
              dashboardChanged={dashboardChanged}
            />
          </div>
        </header>
        <main role="tablist" id="page-tab-view" className="relative min-h-page">
          {children}
        </main>
      </div>
    </div>
  )
}

const Menu = ({
  properties,
  dashboard,
  dashboardChanged,
  logoutMsg,
}: Pick<
  Props,
  "properties" | "dashboard" | "dashboardChanged" | "logoutMsg"
>): JSX.Element => {
  const [menu, setMenu] = React.useState(false)
  const menuRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    function clickOutside({ target }: MouseEvent) {
      isNode(target)
      if (!menuRef.current?.contains(target)) {
        setMenu(false)
      }
    }
    window.addEventListener("click", clickOutside)
    return () => window.removeEventListener("click", clickOutside)
  }, [])

  return (
    <div ref={menuRef} className="relative h-[22px] " data-cy="dashboard">
      <button onClick={() => setMenu(!menu)} className="hover:cursor-pointer">
        <div className="w-[22px] ">
          <Icon.Account dataCy={O.some("dashboard-menu")} />
        </div>
      </button>
      {menu ? (
        <div
          className={
            "absolute z-50 top-8 right-0 card shadow-2xl max-w-lg w-[calc(100vw-32px)] "
          }
        >
          {pipe(
            properties,
            E.match(
              () => <></>,
              props =>
                A.isEmpty(props) ? (
                  <></>
                ) : (
                  <h3 className="heading mb-6">
                    {props.length === 1 ? "Property" : "Properties"}
                  </h3>
                )
            )
          )}

          <div className="flex flex-col items-start gap-3 ">
            {pipe(
              properties,
              E.match(
                () => <></>,
                props => (
                  <>
                    {props.map((property, idx) => (
                      <button
                        className={` w-full px-5 py-3 rounded-md ${pipe(
                          dashboard,
                          E.match(
                            () => "",
                            ({ address }) =>
                              address === property.address
                                ? " bg-tile "
                                : "hover:bg-tile-light"
                          )
                        )}`}
                        data-cy={`dashboard-menu-deal-${idx}`}
                        onClick={() =>
                          pipe(
                            props,
                            A.findFirst(s => s.address === property.address),
                            O.match(
                              () => {},
                              selected =>
                                pipe(
                                  selected.products,
                                  A.findFirst(p => p.type === "marketplace"),
                                  O.match(
                                    () =>
                                      pipe(
                                        selected.products,
                                        A.findFirst(
                                          p => p.type === "transformations"
                                        ),
                                        O.match(
                                          () => {},
                                          x => dashboardChanged(x.propertyCode)
                                        )
                                      ),
                                    x => dashboardChanged(x.propertyCode)
                                  )
                                )
                            )
                          )
                        }
                        key={property.address}
                      >
                        <p className="text-left">{property.address}</p>
                      </button>
                    ))}
                  </>
                )
              )
            )}
          </div>
          <div className="mt-4 flex w-full justify-center">
            <button
              onClick={logoutMsg}
              data-cy="dashboard-menu-logout"
              className="button primary "
            >
              Logout
            </button>
          </div>
        </div>
      ) : (
        <></>
      )}
    </div>
  )
}

const containerStyles = (tab: O.Option<Router.Tab>): string =>
  pipe(
    tab,
    O.match(
      () => "bg-stone-light",
      tab =>
        tab === "feedback" ? "bg-white md:bg-stone-light" : "bg-stone-light"
    )
  )
